/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState, useRef } from 'react';
import { graphql } from 'gatsby';
import { FormattedMessage, useIntl } from 'react-intl';
import { GoArrowRight } from 'react-icons/go';
import { handleRipple } from '../../../helpers/ripple';
import {
  StyledSection,
  StyledContentBlock,
  MainContainer,
  Title,
  ContentContainer,
  ImageSection,
  Image,
  ImageTablet,
  ImageMobile,
  ContentSection,
  SubSection,
  SubMainWrapper,
  SubWrapper,
  LogoImage,
  TextContainer,
  SubText,
  ButtonTextWrapper,
  ButtonText,
  GoArrowButton,
  ButtonTextWrapperMobile,
  CardSection,
  CardLink,
  Card,
  CardTitle,
  CardText,
} from './styled';

const Advertise = () => {
  const { formatMessage } = useIntl();

  const AnimatedNumber = (valueWithSuffix: string, shouldAnimate: boolean) => {
    const [displayValue, setDisplayValue] = useState(valueWithSuffix);
    const startTimeRef = useRef<number | null>(null);
    const requestRef = useRef<number | null>(null);
    const duration = 2000;

    useEffect(() => {
      if (!shouldAnimate) return () => {};

      const match = valueWithSuffix.match(/^([\d.]+)(\D*)$/);
      const endValue = match ? parseFloat(match[1]) : 0;
      const suffix = match ? match[2] : '';

      const step = (timestamp: number) => {
        if (!startTimeRef.current) startTimeRef.current = timestamp;
        const progress = Math.min(
          (timestamp - startTimeRef.current) / duration,
          1,
        );
        const current = progress * endValue;

        const formatted = Number.isInteger(endValue)
          ? Math.floor(current).toLocaleString()
          : current.toLocaleString(undefined, {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            });

        setDisplayValue(`${formatted}${suffix}`);

        if (progress < 1) {
          requestRef.current = requestAnimationFrame(step);
        }
      };

      requestRef.current = requestAnimationFrame(step);

      return () => {
        if (requestRef.current) {
          cancelAnimationFrame(requestRef.current);
        }
      };
    }, [valueWithSuffix, duration, shouldAnimate]);

    return displayValue;
  };

  const IsInViewport = (ref: React.RefObject<HTMLElement>) => {
    const [isIntersecting, setIsIntersecting] = useState(false);

    useEffect(() => {
      const element = ref.current;
      if (!element) return () => {};

      const observer = new IntersectionObserver(
        ([entry]) => setIsIntersecting(entry.isIntersecting),
        { threshold: 0.7 },
      );

      observer.observe(element);

      return () => {
        observer.unobserve(element);
      };
    }, [ref]);

    return isIntersecting;
  };

  const sectionRef = useRef<HTMLDivElement>(null);
  const shouldAnimate = IsInViewport(sectionRef);

  return (
    <StyledSection>
      <StyledContentBlock>
        <MainContainer>
          <Title>
            <FormattedMessage id="home.advertise.title" />
          </Title>
          <ContentContainer>
            <ImageSection>
              <Image
                src={formatMessage({ id: 'home.advertise.image.src' })}
                alt={formatMessage({ id: 'home.advertise.image.alt' })}
                loading="lazy"
              />
              <ImageTablet
                src={formatMessage({ id: 'home.advertise.imageTablet.src' })}
                alt={formatMessage({ id: 'home.advertise.imageTablet.alt' })}
                loading="lazy"
              />
              <ImageMobile
                src={formatMessage({ id: 'home.advertise.imageMobile.src' })}
                alt={formatMessage({ id: 'home.advertise.imageMobile.alt' })}
                loading="lazy"
              />
            </ImageSection>
            <ContentSection>
              <SubSection href={formatMessage({ id: 'home.advertise.link' })}>
                <SubMainWrapper>
                  <SubWrapper>
                    <LogoImage
                      src={formatMessage({
                        id: 'home.advertise.imagelogo.src',
                      })}
                      alt={formatMessage({
                        id: 'home.advertise.imagelogo.alt',
                      })}
                      loading="lazy"
                    />
                    <TextContainer>
                      <SubText>
                        <FormattedMessage id="home.advertise.text" />
                      </SubText>
                      <ButtonTextWrapper>
                        <ButtonText>
                          <FormattedMessage id="home.advertise.button" />
                        </ButtonText>
                        <GoArrowButton onMouseEnter={handleRipple}>
                          <GoArrowRight size={30} color="#1c1c1c" />
                        </GoArrowButton>
                      </ButtonTextWrapper>
                    </TextContainer>
                  </SubWrapper>
                  <ButtonTextWrapperMobile>
                    <ButtonText>
                      <FormattedMessage id="home.advertise.button" />
                    </ButtonText>
                    <GoArrowButton onMouseEnter={handleRipple}>
                      <GoArrowRight size={30} color="#1c1c1c" />
                    </GoArrowButton>
                  </ButtonTextWrapperMobile>
                </SubMainWrapper>
              </SubSection>
              <CardSection>
                <CardLink href={formatMessage({ id: 'home.advertise.link' })}>
                  <Card index={0}>
                    <CardTitle cardIndex={0}>
                      {AnimatedNumber(
                        formatMessage({ id: 'home.advertise.cardList.title1' }),
                        shouldAnimate,
                      )}
                    </CardTitle>
                    <CardText cardIndex={0}>
                      {formatMessage({ id: 'home.advertise.cardList.text1' })}
                    </CardText>
                  </Card>
                </CardLink>

                <CardLink href={formatMessage({ id: 'home.advertise.link' })}>
                  <Card index={1}>
                    <CardTitle cardIndex={1}>
                      {AnimatedNumber(
                        formatMessage({ id: 'home.advertise.cardList.title2' }),
                        shouldAnimate,
                      )}
                    </CardTitle>
                    <CardText cardIndex={1}>
                      {formatMessage({ id: 'home.advertise.cardList.text2' })}
                    </CardText>
                  </Card>
                </CardLink>

                <CardLink href={formatMessage({ id: 'home.advertise.link' })}>
                  <Card index={2}>
                    <CardTitle cardIndex={2}>
                      {AnimatedNumber(
                        formatMessage({ id: 'home.advertise.cardList.title3' }),
                        shouldAnimate,
                      )}
                    </CardTitle>
                    <CardText cardIndex={2}>
                      {formatMessage({ id: 'home.advertise.cardList.text3' })}
                    </CardText>
                  </Card>
                </CardLink>
              </CardSection>
            </ContentSection>
          </ContentContainer>
        </MainContainer>
      </StyledContentBlock>
    </StyledSection>
  );
};

export const query = graphql`
  fragment Advertise on Json {
    advertise {
      title
      text
      button
      link
      image {
        src
        alt
      }
      imageTablet {
        src
        alt
      }
      imageMobile {
        src
        alt
      }
      imagelogo {
        src
        alt
      }
      cardList {
        title1
        text1
        title2
        text2
        title3
        text3
      }
    }
  }
`;

export default Advertise;
